import React from 'react';
import { useSelector } from 'react-redux';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const FundsChart = () => {
  const fundStats = useSelector((s) => s?.adminReducer?.dashboardFundStats);
  const pickAmount = (obj, paths) => {
    for (const path of paths) {
      const value = path.split('.').reduce((acc, key) => acc?.[key], obj);
      if (typeof value === 'number') return value;
    }
    return 0;
  };
  const toMillions = (value) => {
    const amount = Number(value) || 0;
    if (Math.abs(amount) >= 1000000) {
      return amount / 100000000;
    }
    return amount / 1000000;
  };
  // Normalize backend variations
  const rawIn = pickAmount(fundStats, ['fundsIn', 'in', 'totals.fundsIn', 'totals.in', 'summary.fundsIn', 'summary.in']);
  const rawOut = pickAmount(fundStats, ['fundsOut', 'out', 'totals.fundsOut', 'totals.out', 'summary.fundsOut', 'summary.out']);
  let fundsIn = toMillions(rawIn);
  let fundsOut = toMillions(rawOut);
  let total = fundsIn + fundsOut;

  const isZero = total === 0;
  const displayData = isZero
    ? [1, 1] // show ring colors even when values are zero
    : [Number(fundsIn.toFixed(2)), Number(fundsOut.toFixed(2))];

  const data = {
    labels: ['Funds In', 'Funds Out'],
    datasets: [
      {
        data: displayData,
        backgroundColor: [
          '#91CC75',
          '#EE6666',
        ],
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide default legend as we'll create custom one
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            // When total is zero, still show $0.00M in tooltip
            if (isZero) {
              return `${context.label}: $0.00M`;
            }
            return `${context.label}: $${context.raw}M`;
          }
        }
      }
    },
    cutout: '60%',
  };

  const legendItems = [
    { label: 'Funds In:', value: `$${fundsIn.toFixed(2)}M`, color: '#91CC75' },
    { label: 'Funds Out:', value: `$${fundsOut.toFixed(2)}M`, color: '#EE6666' },
  ];

  return (
    <div className="summary w-full p-4 h-[380px] flex flex-col bg-white rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-[20px] font-poppins font-[600] text-[#303030] leading-none">Funds In & Funds Out</h2>
      </div>
      <div className="flex-1 min-h-0 flex">
        {/* Left side - Chart */}
        <div className="w-1/2 relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-[16px] font-[600] text-[#303030]">Total</div>
              <div className="text-[20px] font-[700] text-[#303030]">${total.toFixed(2)}M</div>
            </div>
          </div>
          <Pie data={data} options={options} />
        </div>
        
        {/* Right side - Custom Legend */}
        <div className="w-1/2 flex flex-col justify-center gap-8 pl-8">
          {legendItems.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-8 h-4 rounded-sm" style={{ backgroundColor: item.color }}></div>
              <span className="text-[14px] font-poppins font-[600] text-[#303030]">{item.label}</span>
              <span className="text-[14px] font-poppins text-[#8C8C8C]">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FundsChart; 
